import { EVERYONE_ID, MENTION_ALL_ATTR, MENTION_ATTR, mentionHtml } from "./mentions";
import { isFormatClass } from "./richFormat";
import { sanitizeHtml } from "./sanitizeHtml";

/**
 * The rich composer's HTML contract. What the composer sends is only:
 *
 *   <p> <br> <strong> <em> <u> <s> <code> <pre><code> <ul>/<ol>/<li> <blockquote> <hr>
 *   <a href target rel>  and the mention chip of lib/mentions.ts
 *   <span class="fg-c-ID">  <mark class="fg-h-ID">  <span class="fg-s-ID">   (ids: Shared/richformat/format.json)
 *
 * No style attributes, no other classes, no other tags. `serializeRich` turns whatever the
 * contenteditable holds (browser <b>/<i>/<strike>/<div>/<font>, pasted markup) into exactly that.
 */

const ZERO_WIDTH = /[\u200B]/g;

export function escapeText(value: string): string {
    return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function escapeAttr(value: string): string {
    return escapeText(value).replace(/"/g, "&quot;");
}

const BLOCK_TAGS = new Set([
    "P", "DIV", "H1", "H2", "H3", "H4", "H5", "H6", "UL", "OL", "LI", "BLOCKQUOTE", "PRE", "HR",
    "TABLE", "THEAD", "TBODY", "TFOOT", "TR", "TD", "TH", "SECTION", "ARTICLE", "HEADER", "FOOTER", "FIGURE"
]);

export interface SerializeOptions {
    /** Keep <img> (http(s) or data:image src). Used when an existing message is edited, so its pictures survive. */
    keepImages?: boolean;
}

function isElement(n: Node): n is HTMLElement {
    return n.nodeType === 1;
}

function isBlock(n: Node): boolean {
    return isElement(n) && BLOCK_TAGS.has(n.tagName);
}

function containsBlock(el: Element): boolean {
    for (const child of Array.from(el.childNodes)) {
        if (isBlock(child)) return true;
        if (isElement(child) && containsBlock(child)) return true;
    }
    return false;
}

function safeHref(href: string | null): string | null {
    const value = (href ?? "").trim();
    return /^(?:https?:\/\/|mailto:)/i.test(value) ? value : null;
}

function safeImageSrc(src: string | null): string | null {
    const value = (src ?? "").trim();
    return /^(?:https?:\/\/|data:image\/(?:png|gif|jpe?g|webp);base64,)/i.test(value) ? value : null;
}

function wrap(tag: string, inner: string, attrs = ""): string {
    return inner ? `<${tag}${attrs}>${inner}</${tag}>` : "";
}

function formatClassesOf(el: HTMLElement): string[] {
    return Array.from(el.classList).filter(isFormatClass);
}

function isUnboldedWrapper(el: HTMLElement): boolean {
    // Google Docs wraps pasted text in <b style="font-weight:normal">.
    return /font-weight\s*:\s*(?:normal|400)/i.test(el.getAttribute("style") ?? "");
}

function inlineNode(node: Node, o: SerializeOptions): string {
    if (node.nodeType === 3) {
        const data = (node.nodeValue ?? "").replace(ZERO_WIDTH, "").replace(/\u00a0/g, " ").replace(/[\r\n\t]+/g, " ");
        return escapeText(data);
    }
    if (!isElement(node)) return "";
    const el = node;
    const tag = el.tagName;
    if (tag === "BR") return "<br>";
    if (el.hasAttribute(MENTION_ALL_ATTR)) return mentionHtml({ userId: EVERYONE_ID, name: "everyone" });
    if (el.hasAttribute(MENTION_ATTR)) {
        const name = (el.textContent ?? "").replace(ZERO_WIDTH, "").replace(/^@/, "").trim();
        return name ? mentionHtml({ userId: el.getAttribute(MENTION_ATTR) ?? "", name }) : "";
    }
    if (tag === "IMG") {
        if (!o.keepImages) return "";
        const src = safeImageSrc(el.getAttribute("src"));
        return src ? `<img src="${escapeAttr(src)}" alt="${escapeAttr(el.getAttribute("alt") ?? "")}">` : "";
    }
    // A wrapper that holds blocks is transparent: its blocks are handled by `blocks`, here we only get its text.
    if (isBlock(el)) return inlineChildren(el, o) + "<br>";
    const inner = inlineChildren(el, o);
    switch (tag) {
        case "STRONG": return wrap("strong", inner);
        case "B": return isUnboldedWrapper(el) ? inner : wrap("strong", inner);
        case "EM":
        case "I": return wrap("em", inner);
        case "U": return wrap("u", inner);
        case "S":
        case "STRIKE":
        case "DEL": return wrap("s", inner);
        case "CODE": return wrap("code", inner);
        case "A": {
            const href = safeHref(el.getAttribute("href"));
            return href && inner
                ? `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer">${inner}</a>`
                : inner;
        }
        case "MARK": {
            const cls = formatClassesOf(el).find((c) => c.startsWith("fg-h-"));
            return cls ? wrap("mark", inner, ` class="${cls}"`) : inner;
        }
        case "SPAN": {
            // Colour and size nest (a span carrying both becomes two spans).
            let out = inner;
            for (const cls of formatClassesOf(el)) {
                if (cls.startsWith("fg-h-")) out = wrap("mark", out, ` class="${cls}"`);
                else out = wrap("span", out, ` class="${cls}"`);
            }
            return out;
        }
        default: return inner;
    }
}

function inlineChildren(el: Node, o: SerializeOptions): string {
    let out = "";
    for (const child of Array.from(el.childNodes)) out += inlineNode(child, o);
    return out;
}

function stripTrailingBreak(html: string): string {
    return html.replace(/(?:<br>)+$/, (m) => (m.length > 4 ? m.slice(4) : ""));
}

function hasVisible(html: string): boolean {
    return /<br>|<img|<hr|data-fg-mention/.test(html) || /[^\s]/.test(html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " "));
}

function preText(el: Node): string {
    let out = "";
    const children = Array.from(el.childNodes);
    children.forEach((child, i) => {
        if (child.nodeType === 3) out += (child.nodeValue ?? "").replace(ZERO_WIDTH, "").replace(/\u00a0/g, " ").replace(/\r\n?/g, "\n");
        else if (isElement(child)) {
            if (child.tagName === "BR") out += "\n";
            else if (isBlock(child)) {
                out += preText(child);
                if (i < children.length - 1) out += "\n";
            } else out += preText(child);
        }
    });
    return out;
}

function liContent(li: Element, o: SerializeOptions): string {
    let out = "";
    let pendingBreak = false;
    for (const child of Array.from(li.childNodes)) {
        if (isElement(child) && (child.tagName === "UL" || child.tagName === "OL")) {
            out += blockElement(child, o);
            pendingBreak = false;
        } else if (isElement(child) && (child.tagName === "P" || child.tagName === "DIV")) {
            if (pendingBreak) out += "<br>";
            out += stripTrailingBreak(inlineChildren(child, o));
            pendingBreak = true;
        } else {
            out += inlineNode(child, o);
        }
    }
    return stripTrailingBreak(out);
}

function listElement(el: Element, o: SerializeOptions): string {
    const tag = el.tagName.toLowerCase();
    let items = "";
    for (const child of Array.from(el.childNodes)) {
        if (!isElement(child)) continue;
        if (child.tagName === "LI") {
            const inner = liContent(child, o);
            if (hasVisible(inner)) items += `<li>${inner}</li>`;
        } else if (child.tagName === "UL" || child.tagName === "OL") {
            // <ul><ul>…</ul></ul> (an indent) is a list inside an item.
            const nested = listElement(child, o);
            if (nested) items += `<li>${nested}</li>`;
        }
    }
    return items ? `<${tag}>${items}</${tag}>` : "";
}

function blockElement(el: Element, o: SerializeOptions): string {
    switch (el.tagName) {
        case "UL":
        case "OL": return listElement(el, o);
        case "BLOCKQUOTE": {
            const inner = blocks(el, o);
            return inner ? `<blockquote>${inner}</blockquote>` : "";
        }
        case "PRE": {
            const text = preText(el).replace(/\n+$/, "");
            return text ? `<pre><code>${escapeText(text)}</code></pre>` : "";
        }
        case "HR": return "<hr>";
        case "LI": return `<ul><li>${liContent(el, o)}</li></ul>`;
        case "TABLE": case "THEAD": case "TBODY": case "TFOOT": case "TR": case "SECTION": case "ARTICLE":
            return blocks(el, o);
        default: {
            if (containsBlock(el)) return blocks(el, o);
            const inner = stripTrailingBreak(inlineChildren(el, o));
            const heading = /^H[1-6]$/.test(el.tagName);
            if (!hasVisible(inner)) return "<p><br></p>";
            return `<p>${heading ? wrap("strong", inner) : inner}</p>`;
        }
    }
}

function blocks(parent: Node, o: SerializeOptions): string {
    let out = "";
    let buffer = "";
    const flush = () => {
        const inner = stripTrailingBreak(buffer);
        buffer = "";
        if (/[^\s]/.test(inner.replace(/<br>/g, "")) || /<img|data-fg-mention/.test(inner)) out += `<p>${inner}</p>`;
    };
    for (const child of Array.from(parent.childNodes)) {
        if (isBlock(child) || (isElement(child) && containsBlock(child))) {
            flush();
            if (isBlock(child)) out += blockElement(child as Element, o);
            else out += blocks(child, o);
        } else {
            buffer += inlineNode(child, o);
        }
    }
    flush();
    return out;
}

function trimEmptyParagraphs(html: string): string {
    return html.replace(/^(?:<p><br><\/p>)+/, "").replace(/(?:<p><br><\/p>)+$/, "");
}

/** Editor (or any) DOM -> the composer's allowed HTML. "" when there is nothing to send. */
export function serializeRich(root: Node, options: SerializeOptions = {}): string {
    return trimEmptyParagraphs(blocks(root, options));
}

/** Same, for an HTML string (parsed in an inert document). */
export function serializeRichHtml(html: string, options: SerializeOptions = {}): string {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return serializeRich(doc.body, options);
}

// ── Markdown typed into the box ──────────────────────────────────────────────

const URL_PATTERN = /(?<![">=\w])(https?:\/\/[^\s<>"]+)/g;

/** Inline markdown over already-escaped text; returns HTML (or the input when nothing matched). */
export function inlineMarkdown(escaped: string): string {
    const stash: string[] = [];
    const hold = (html: string) => `${stash.push(html) - 1}`;
    let s = escaped;
    s = s.replace(/`([^`\n]+)`/g, (_m, c: string) => hold(`<code>${c}</code>`));
    s = s.replace(/\[([^\]\n]+)\]\((https?:\/\/[^)\s]+)\)/g, (_m, t: string, u: string) =>
        hold(`<a href="${u}" target="_blank" rel="noopener noreferrer">${t}</a>`));
    s = s.replace(URL_PATTERN, (m) => {
        const trail = /[.,;:!?)\]]+$/.exec(m)?.[0] ?? "";
        const url = trail ? m.slice(0, -trail.length) : m;
        return hold(`<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`) + trail;
    });
    s = s.replace(/\*\*\*([^*\n]+?)\*\*\*/g, "<strong><em>$1</em></strong>");
    s = s.replace(/\*\*([^*\n]+?)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/(?<![\w*])__([^_\n]+?)__(?![\w*_])/g, "<strong>$1</strong>");
    s = s.replace(/(?<![\w*])\*([^*\s](?:[^*\n]*[^*\s])?)\*(?![\w*])/g, "<em>$1</em>");
    s = s.replace(/(?<![\w_])_([^_\s](?:[^_\n]*[^_\s])?)_(?![\w_])/g, "<em>$1</em>");
    s = s.replace(/~~([^~\n]+?)~~/g, "<s>$1</s>");
    return s.replace(/(\d+)/g, (_m, i: string) => stash[Number(i)] ?? "");
}

const SKIP_INLINE = new Set(["CODE", "PRE", "A"]);

/**
 * `**bold**`, `_italic_`, `~~strike~~`, `` `code` ``, `[text](url)` and bare links typed in the box
 * become formatting when the message is sent, so people who type Markdown still get it. Text inside
 * code, links and mention chips is left alone, as is anything already formatted by the toolbar.
 * Also: paragraphs starting "- " / "1. " / "> " become a list / quote.
 */
export function applyMarkdownShortcuts(html: string): string {
    if (!html) return html;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const body = doc.body;

    // Block shortcuts on plain one-line paragraphs, grouped when consecutive.
    const kids = Array.from(body.children);
    let i = 0;
    while (i < kids.length) {
        const p = kids[i]!;
        const kind = plainParagraphKind(p);
        if (!kind) { i++; continue; }
        let j = i;
        const group: Element[] = [];
        while (j < kids.length && plainParagraphKind(kids[j]!) === kind) { group.push(kids[j]!); j++; }
        const holder = doc.createElement(kind === "ul" ? "ul" : kind === "ol" ? "ol" : "blockquote");
        for (const g of group) {
            const text = (g.textContent ?? "").replace(/^(?:[*-]|\d+\.|>)\s+/, "");
            if (kind === "quote") {
                const inner = doc.createElement("p");
                inner.textContent = text;
                holder.appendChild(inner);
            } else {
                const li = doc.createElement("li");
                li.textContent = text;
                holder.appendChild(li);
            }
        }
        group[0]!.replaceWith(holder);
        group.slice(1).forEach((g) => g.remove());
        i = j;
    }

    const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT);
    const texts: Text[] = [];
    for (let n = walker.nextNode(); n; n = walker.nextNode()) texts.push(n as Text);
    for (const t of texts) {
        let skip = false;
        for (let p = t.parentElement; p && p !== body; p = p.parentElement) {
            if (SKIP_INLINE.has(p.tagName) || p.hasAttribute(MENTION_ATTR) || p.hasAttribute(MENTION_ALL_ATTR)) { skip = true; break; }
        }
        if (skip) continue;
        const escaped = escapeText(t.nodeValue ?? "");
        const converted = inlineMarkdown(escaped);
        if (converted === escaped) continue;
        const tpl = doc.createElement("template");
        tpl.innerHTML = converted;
        t.replaceWith(tpl.content);
    }
    return body.innerHTML;
}

function plainParagraphKind(el: Element): "ul" | "ol" | "quote" | null {
    if (el.tagName !== "P" || el.children.length > 0) return null;
    const text = el.textContent ?? "";
    if (/^[*-]\s+\S/.test(text)) return "ul";
    if (/^\d+\.\s+\S/.test(text)) return "ol";
    if (/^>\s+\S/.test(text)) return "quote";
    return null;
}

/** What gets sent: the editor's content, serialised, with typed Markdown applied. */
export function composeSendHtml(root: Node, options: SerializeOptions = {}): string {
    return applyMarkdownShortcuts(serializeRich(root, options));
}

// ── Plain text, emptiness, drafts ────────────────────────────────────────────

function textWalk(node: Node, out: string[]): void {
    if (node.nodeType === 3) { out.push((node.nodeValue ?? "").replace(ZERO_WIDTH, "").replace(/\u00a0/g, " ")); return; }
    if (!isElement(node)) return;
    if (node.tagName === "BR") { out.push("\n"); return; }
    const block = isBlock(node);
    if (block) out.push("\n");
    for (const child of Array.from(node.childNodes)) textWalk(child, out);
    if (block) out.push("\n");
}

/** Plain text of editor DOM or an HTML string: for snippets, notification text and "is it empty". */
export function richToPlain(source: Node | string): string {
    const root = typeof source === "string" ? new DOMParser().parseFromString(source, "text/html").body : source;
    const out: string[] = [];
    textWalk(root, out);
    return out.join("").replace(/[ \t]*\n[ \t]*/g, "\n").replace(/\n{2,}/g, "\n").trim();
}

/** True when the HTML has no text, no picture, no rule and no mention. */
export function richIsEmpty(html: string): boolean {
    if (!html) return true;
    if (/<(?:img|hr)\b|data-fg-mention/i.test(html)) return false;
    return richToPlain(html).length === 0;
}

/** A saved draft may be plain text (older versions) or HTML (the rich composer). Returns editor-ready, sanitised HTML. */
export function draftToHtml(draft: string): string {
    if (!draft) return "";
    if (/^\s*</.test(draft)) return sanitizeHtml(draft);
    return draft
        .split(/\r?\n/)
        .map((line) => (line ? `<p>${escapeText(line)}</p>` : "<p><br></p>"))
        .join("");
}

/** One line of text for a draft preview (Chat list). */
export function draftPreview(draft: string): string {
    const text = /^\s*</.test(draft) ? richToPlain(draft) : draft;
    return text.replace(/\s+/g, " ").trim();
}
